/**
 * Who is signed in, in one place. Task 30.
 *
 * **The client never decides whether a session is valid** — it asks. `GET /auth/me` is the
 * only source of truth, because the session lives in an `HttpOnly` cookie that JavaScript
 * cannot read and is checked server-side on every request (`require_principal`: present,
 * not revoked, not past its absolute expiry, not idle-expired — four checks, none of them
 * visible from here). Anything this module cached and trusted would be a second opinion
 * about a question the server has already answered, and the two would disagree exactly when
 * it matters: after a revoke.
 *
 * ## Why `me()` is a polling query and not a one-shot bootstrap
 *
 * The first version of this file called `me()` once on mount and kept the answer. **Measured
 * against a real service, that let the app sit on a revoked session indefinitely**: the
 * session was revoked in the database, `/auth/me` began answering 401, and the console went
 * on rendering a signed-in shell because nothing on screen happened to make an authenticated
 * request. The only polling query in the app at that point was `/health/deep`, which
 * `API-Spec.md` makes unauthenticated by design, so it answered 200 forever.
 *
 * `AuthN-AuthZ#sessions` lists four ways a session ends and every one of them is server-side
 * and silent: an idle timeout, the absolute expiry, a revoke from another browser, and
 * "revoke everything" during an incident. A client that only asks once cannot see any of
 * them. So the session is re-checked on an interval and on window focus — focus especially,
 * because the revoke-while-you-were-away case is the common one, and coming back to the tab
 * is exactly when the answer should be refreshed.
 *
 * ## Three states, and the third one matters
 *
 * - `checking` — the first `me()` is in flight. **Not the same as signed out.** Treating it
 *   as signed out flashes the login screen on every page load for anyone with a valid
 *   cookie, and replaces the URL they asked for.
 * - `in` — with the account.
 * - `out` — `me()` answered 401, or a 401 anywhere else routed through `sessionLost`.
 *
 * `isError` is checked rather than only `data`, for the reason the System screen taught:
 * TanStack Query keeps the last successful `data` alongside an error, so reading `data`
 * alone would report the account of a session that has just been refused.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { api } from '../api/client';
import { isUnauthenticated } from '../api/errors';
import type { AccountResponse } from '../api/generated';
import { onSessionLost } from './sessionLost';

export type AuthStatus = 'checking' | 'in' | 'out';

/** The query key the 401 path writes to. One place, so the two cannot drift. */
export const ME_QUERY_KEY = ['auth', 'me'] as const;

/**
 * How often the session is re-validated while a tab is open. Two minutes is a compromise:
 * often enough that a revoke during an incident is noticed while the operator is still
 * looking at the screen, rare enough that it is not a request per screen per minute. Focus
 * is the other trigger and is the one that catches the long-away case.
 */
const RECHECK_MS = 120_000;

export interface AuthState {
  status: AuthStatus;
  account: AccountResponse | null;
  /** Re-ask the server. Called after a login completes. */
  refresh: () => Promise<void>;
  /**
   * Ends the session server-side, then locally.
   *
   * `everywhere` revokes **every** session for the account, which is the control
   * `AuthN-AuthZ-and-User-Management.md` names as the reason sessions are rows at all: *"'revoke
   * everything now' is a control I want to actually work during an incident."*
   */
  signOut: (everywhere?: boolean) => Promise<void>;
  /** Called by the 401 handler. Local only — the server has already refused. */
  forgetSession: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const queryClient = useQueryClient();

  const me = useQuery({
    queryKey: ME_QUERY_KEY,
    // `null` rather than a throw for the expected case. Nobody being signed in is not an
    // error condition — it is the answer — and letting it throw would put a permanent red
    // entry in the query cache for the normal state of a logged-out browser.
    queryFn: async (): Promise<AccountResponse | null> => {
      try {
        return await api.auth.me();
      } catch (error) {
        if (isUnauthenticated(error)) return null;
        throw error;
      }
    },
    retry: false,
    refetchInterval: RECHECK_MS,
    refetchOnWindowFocus: true,
    // Refetching while the tab is hidden buys nothing: nobody is reading the screen, and the
    // focus refetch covers the moment they come back.
    refetchIntervalInBackground: false,
  });

  const status: AuthStatus = me.isPending
    ? 'checking'
    : me.isError || !me.data
      ? 'out'
      : 'in';

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
  }, [queryClient]);

  const forgetSession = useCallback(() => {
    // Written directly rather than invalidated. Invalidating would refetch, the refetch
    // would 401, the 401 would route back here — a loop. Setting the answer is both correct
    // (the server has just told us) and terminal.
    queryClient.setQueryData(ME_QUERY_KEY, null);
  }, [queryClient]);

  // Dropping everything else is driven off the *state*, not off `forgetSession`, so it
  // happens on both routes into it: a 401 from some other request, and the periodic `me()`
  // poll discovering the session has been revoked. Hanging it on the callback alone left the
  // second path clearing nothing.
  useEffect(() => {
    if (status !== 'out') return;
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== ME_QUERY_KEY[0],
    });
  }, [status, queryClient]);

  const signOut = useCallback(async (everywhere = false) => {
    try {
      // ⚠ **`/auth/logout` for both, rather than DELETE per session.** Only logout clears the
      // cookies; `DELETE /auth/sessions/{id}` revokes the row and leaves the browser holding a
      // token that now 401s, which presents as a UI that looks signed in until the next request.
      await api.auth.logout({ requestBody: { everywhere } });
    } finally {
      // Locally regardless. If the request failed the cookie may still be live, but the
      // operator asked to be signed out and the UI must not keep showing their data while
      // it argues with the server about it.
      forgetSession();
    }
  }, [forgetSession]);

  // The other half of the single 401 handler — see `sessionLost.ts`. Every query and every
  // mutation routes here, so a screen written next year inherits it without knowing.
  useEffect(() => onSessionLost(forgetSession), [forgetSession]);

  const value = useMemo(
    () => ({ status, account: me.data ?? null, refresh, signOut, forgetSession }),
    [status, me.data, refresh, signOut, forgetSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth called outside AuthProvider');
  return context;
}
